import React, { useState } from 'react';

import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { post_payout_held_resolve } from '../api';
import {
  AMOUNT_DIFFERS_CHECK_LABEL,
  REASON_MAX_LEN,
  RECOVERY_UNKNOWN_TEXT,
  bad_utr,
  bank_rejected,
  count_as_sent_blocked_text,
  ignore_blocked_text,
  held_amounts_differ,
  confirm_error,
  confirm_fields,
  confirm_needs,
  has_error,
  held_confirm_paise,
  held_confirm_summary,
  ops_actions_for,
  recover_banner_text,
  recover_confirm_text,
  recovery_stop_warning,
  resolve_done_text,
  resolve_errors
} from './adapters';
import { ACTION_FAILED, ACTION_RECOVER, ACTION_SENT, resolve_body } from './requests';
import useIdempotentPost from './use_idempotent_post';
import ConfirmStep from './confirm_step';

const FAILED_TEXT = 'The line may not have been resolved. Reload the held lines to check.';

const ResolveDialog = ({ line, today, on_close, on_done, on_stale, on_error }) => {
  const [form, setForm] = useState({
    action: '',
    reason: '',
    bank_reference: '',
    sent_on: '',
    payout_item_uuid: ''
  });
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [step2, setStep2] = useState({ amount: '', checked: false });
  const [error, setError] = useState('');
  // the recovery the server reported on refusal (a typed payment ID, or a line the page could not
  // check).
  const [server_effect, setServerEffect] = useState(null);
  const { busy, send } = useIdempotentPost(FAILED_TEXT);
  const shown = server_effect ? { ...line, recovery_effect: server_effect } : line;
  const stops_recovery = form.action === ACTION_FAILED && !!shown.recovery_effect;
  const errors = resolve_errors(line, form, today);
  const recover = form.action === ACTION_RECOVER;
  const sent = form.action === ACTION_SENT || recover;
  const actions = ops_actions_for(line);
  const differs_text = count_as_sent_blocked_text(line);
  const no_ignore_text = ignore_blocked_text(line);
  // The stored copy of a bad UTR is not offered as the blank default: the server needs it typed.
  const own_utr = bad_utr(line) ? '' : line.bank_reference;
  const needs_item = !line.payout_item_uuid && form.action && form.action !== 'IGNORE' && !recover;
  const needs = confirm_needs(form.action);
  const step2_error = confirm_error(needs, step2, held_confirm_paise(line));
  const set = (k) => (e) => {
    setConfirming(false);
    setServerEffect(null);
    setStep2({ amount: '', checked: false });
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };
  const field_error = (k) =>
    touched && errors[k] ? (
      <span className="tp-neg" role="alert">
        {errors[k]}
      </span>
    ) : null;
  const action_label = (actions.find((a) => a.value === form.action) || {}).label || '';
  const confirm_text = recover ? recover_confirm_text(line) : `Yes, ${action_label.toLowerCase()}`;

  const submit = () => {
    const body = resolve_body({
      ...form,
      line_id: line.line_id,
      recover_pay_month: line.recover_pay_month,
      confirm: confirm_fields(needs, step2),
      recovery_stop_confirmed: stops_recovery
    });
    send(body, () => post_payout_held_resolve(body)).then((r) => {
      if (r.ok) {
        on_done(resolve_done_text(r.data, form.action));
        return;
      }
      if (r.err.kind === 'recovery_stop') {
        // Stay on step 2, now with the warning: Ops tick the check again to confirm the stop.
        setServerEffect(r.err.effect);
        setStep2({ amount: '', checked: false });
        setError(r.err.text);
        return;
      }
      if (r.err.kind === 'auth') on_error(r.raw, FAILED_TEXT);
      if (r.err.kind === 'stale') on_stale(r.err.text);
      setError(r.err.text);
      setConfirming(false);
    });
  };

  return (
    <TpDialog
      title={`Held line ${line.line_no} · ${line.amount}`}
      on_close={on_close}
      busy={busy}
      guard_text={discard_guard_text(form.reason)}
    >
      <div className="tp-modal tp-po-resolve">
        <div className="tp-ctx">
          <StatusPill tone={line.proposed.tone}>{line.proposed.label}</StatusPill>{' '}
          {line.beneficiary_name}
          {line.bank_reason ? ` · bank: ${line.bank_reason}` : ''}
          {line.teacher_name ? ` · ${line.teacher_name} ${line.pay_month}` : ''}
        </div>
        {confirming && needs.step ? (
          <ConfirmStep
            id="tp-po-res-confirm"
            action={form.action}
            needs={needs}
            summary={held_confirm_summary(shown, form.action)}
            warn={stops_recovery}
            input={step2}
            on_input={setStep2}
            error={step2.amount.trim() || step2.checked ? step2_error : ''}
            check_label={
              form.action === ACTION_FAILED && held_amounts_differ(line) && !bank_rejected(line)
                ? AMOUNT_DIFFERS_CHECK_LABEL
                : ''
            }
          />
        ) : (
          <>
            {differs_text ? <div className="tp-banner tp-banner-warn">{differs_text}</div> : null}
            {no_ignore_text ? (
              <div className="tp-banner tp-banner-warn">{no_ignore_text}</div>
            ) : null}
            <fieldset className="tp-po-radios">
              <legend className="tp-hint">What should happen to this line?</legend>
              {actions.map((a) => (
                <label key={a.value} htmlFor={`tp-po-act-${a.value}`}>
                  <input
                    id={`tp-po-act-${a.value}`}
                    type="radio"
                    name="tp-po-action"
                    value={a.value}
                    checked={form.action === a.value}
                    onChange={set('action')}
                    disabled={busy}
                  />{' '}
                  {a.label}
                </label>
              ))}
            </fieldset>
            {field_error('action')}
            {stops_recovery ? (
              <div className="tp-banner tp-banner-warn" role="alert">
                {recovery_stop_warning(shown)}
              </div>
            ) : null}
            {form.action === ACTION_FAILED &&
            !stops_recovery &&
            line.recovery_effect_unavailable ? (
              <div className="tp-banner tp-banner-warn" role="alert">
                {RECOVERY_UNKNOWN_TEXT}
              </div>
            ) : null}
            {sent ? (
              <div className="tp-field-2">
                <div className="tp-field">
                  <label htmlFor="tp-po-res-utr">
                    Bank reference (UTR){own_utr ? `, blank = ${own_utr}` : ''}
                  </label>
                  <input
                    id="tp-po-res-utr"
                    className="tp-input tp-mono"
                    value={form.bank_reference}
                    onChange={set('bank_reference')}
                    maxLength={64}
                  />
                  {bad_utr(line) ? (
                    <span className="tp-hint">
                      The bank&apos;s reference
                      {line.bank_reference ? ` (${line.bank_reference})` : ''} is not valid: type
                      the correct UTR.
                    </span>
                  ) : null}
                  {field_error('bank_reference')}
                </div>
                <div className="tp-field">
                  <label htmlFor="tp-po-res-date">
                    Sent on{line.value_date ? `, blank = ${line.value_date}` : ''}
                  </label>
                  <input
                    id="tp-po-res-date"
                    className="tp-input"
                    type="date"
                    max={today}
                    value={form.sent_on}
                    onChange={set('sent_on')}
                  />
                  {field_error('sent_on')}
                </div>
              </div>
            ) : null}
            {needs_item ? (
              <div className="tp-field">
                <label htmlFor="tp-po-res-item">Payment ID this line answers</label>
                <input
                  id="tp-po-res-item"
                  className="tp-input tp-mono"
                  value={form.payout_item_uuid}
                  onChange={set('payout_item_uuid')}
                />
                {field_error('payout_item_uuid')}
              </div>
            ) : null}
            <div className="tp-field">
              <label htmlFor="tp-po-res-reason">Reason (recorded)</label>
              <textarea
                id="tp-po-res-reason"
                className="tp-textarea"
                value={form.reason}
                maxLength={REASON_MAX_LEN * 2}
                onChange={set('reason')}
              />
              {field_error('reason')}
            </div>
          </>
        )}
        {confirming && !needs.step ? (
          <div className="tp-banner tp-banner-warn" role="alert">
            {recover
              ? recover_banner_text(line)
              : `${action_label} line ${line.line_no} (${line.amount})? This cannot be undone here.`}
          </div>
        ) : null}
        {error ? (
          <div className="tp-neg" role="alert">
            {error}
          </div>
        ) : null}
        <div className="tp-modal-ft">
          {confirming && needs.step ? (
            <button
              type="button"
              className="tp-btn-ghost"
              onClick={busy ? undefined : () => setConfirming(false)}
            >
              Back
            </button>
          ) : (
            <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_close}>
              Cancel
            </button>
          )}
          {confirming ? (
            <button
              type="button"
              className="tp-btn"
              onClick={busy || (needs.step && step2_error) ? undefined : submit}
              disabled={needs.step && !!step2_error}
              aria-disabled={busy ? 'true' : undefined}
            >
              {busy ? 'Saving…' : confirm_text}
            </button>
          ) : (
            <button
              type="button"
              className="tp-btn-out"
              onClick={() => {
                setTouched(true);
                if (!has_error(errors)) setConfirming(true);
              }}
            >
              Resolve…
            </button>
          )}
        </div>
      </div>
    </TpDialog>
  );
};

export default ResolveDialog;
